/**
 * Finding widget targets on a host page and reading their configuration.
 *
 * Kept free of React and the DOM-mutating side of mounting so it can be tested
 * against plain elements.
 */

const ID_PREFIX = 'omnigo-';

// A plain div survives Squarespace and WordPress editors; a custom element may
// not. `data-omnigo-widget` is the explicit form, the id prefix the convenient
// one.
const WIDGET_SELECTORS = ['[data-omnigo-widget]', `[id^="${ID_PREFIX}"]`];

/**
 * Resolves which widget an element is asking for.
 *
 * `data-omnigo-widget` wins over the id so an element can carry an id for the
 * host page's own purposes. Returns null when no name is expressed, which the
 * caller reports rather than guessing.
 */
const widgetNameFromElement = (el) => {
    const explicit = el?.dataset?.omnigoWidget?.trim();
    if (explicit) {
        return explicit.toLowerCase();
    }

    const id = el?.id ?? '';
    if (!id.toLowerCase().startsWith(ID_PREFIX)) {
        return null;
    }

    const name = id.slice(ID_PREFIX.length).trim();
    return name ? name.toLowerCase() : null;
};

/**
 * Reads a target's `data-*` attributes into widget props.
 *
 * Everything is passed through, so a widget can read its own attributes
 * without a change here. `regProvider` is lower-cased because provider lookup
 * is case-insensitive and `data-reg-provider="BikeReg"` should work.
 */
const parseConfig = (dataset = {}) => {
    const { omnigoWidget, ...rest } = dataset;
    return {
        ...rest,
        ...(rest.regProvider ? { regProvider: rest.regProvider.trim().toLowerCase() } : {})
    };
};

// De-duplicated because an element carrying both a data attribute and a
// prefixed id matches twice.
const findTargets = (root = document) => [
    ...new Set(WIDGET_SELECTORS.flatMap(selector => [...root.querySelectorAll(selector)]))
];

export {
    ID_PREFIX,
    WIDGET_SELECTORS,
    widgetNameFromElement,
    parseConfig,
    findTargets
};
